import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useAuth } from '@/contexts/AuthContext';

const VISIBLE_MS = 7000;     // auto-dismiss after 7s
const MAX_BUBBLES = 3;       // stack at most 3 — older ones drop off

/**
 * Floating, dismissible chat-bubble toasts that appear when an organization
 * member sends you a message. Click the bubble to jump to /messages with the
 * conversation auto-opened.
 *
 * Renders globally inside AppLayout — listens on the existing `new_message`
 * WebSocket event emitted by ``routes/groups.py::send_message``.
 */
export function MessageBubbleToaster() {
  const { addListener } = useWebSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bubbles, setBubbles] = useState([]);

  const dismiss = useCallback((id) => {
    setBubbles(prev => prev.filter(b => b.id !== id));
  }, []);

  useEffect(() => {
    if (!addListener || !user) return;
    const unsub = addListener('new_message', (_event, data) => {
      // If user is already on /messages with this conversation open, skip the popup
      if (location.pathname === '/messages' && document.visibilityState === 'visible') {
        return;
      }
      const bubble = {
        id: `${data.message_id || Date.now()}-${Math.random()}`,
        sender_id: data.sender_id,
        sender_name: data.sender_name || 'Teammate',
        content: data.content || '',
        created_at: data.created_at || new Date().toISOString(),
      };
      setBubbles(prev => [bubble, ...prev].slice(0, MAX_BUBBLES));
      // Auto-dismiss
      setTimeout(() => dismiss(bubble.id), VISIBLE_MS);
      // Play soft notification ping (respects browser autoplay restrictions)
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 880; o.type = 'sine';
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
        o.start(); o.stop(ctx.currentTime + 0.25);
      } catch { /* ignore — sound is optional */ }
    });
    return unsub;
  }, [addListener, user, dismiss, location.pathname]);

  if (bubbles.length === 0) return null;

  const handleOpen = (b) => {
    navigate(`/messages?dm=${b.sender_id}`);
    dismiss(b.id);
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] flex flex-col-reverse gap-2 max-w-xs sm:max-w-sm pointer-events-none"
      data-testid="message-bubble-toaster"
      aria-live="polite"
    >
      {bubbles.map((b, idx) => (
        <button
          key={b.id}
          type="button"
          onClick={() => handleOpen(b)}
          style={{ animation: 'msgBubbleSlide 240ms cubic-bezier(0.16, 1, 0.3, 1)' }}
          className="pointer-events-auto group relative w-full bg-white dark:bg-slate-900 border border-[#0047AB]/30 dark:border-[#3B82F6]/40 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,71,171,0.45)] p-3.5 text-left hover:scale-[1.02] hover:shadow-[0_15px_50px_-10px_rgba(0,71,171,0.6)] transition-all"
          data-testid={`message-bubble-${idx}`}
        >
          {/* Slack/chat-style tail pointing down-right */}
          <span
            aria-hidden
            className="absolute -bottom-2 right-7 w-4 h-4 bg-white dark:bg-slate-900 border-r border-b border-[#0047AB]/30 dark:border-[#3B82F6]/40 rotate-45"
          />
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); dismiss(b.id); }}
            className="absolute -top-2 -right-2 w-6 h-6 inline-flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
            aria-label="Dismiss"
            data-testid={`message-bubble-dismiss-${idx}`}
          >
            <X className="w-3 h-3" />
          </span>
          <div className="flex items-start gap-2.5">
            <div className="shrink-0 relative">
              <MemberAvatar userId={b.sender_id} name={b.sender_name} size="sm" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-[#0047AB] rounded-full border-2 border-white dark:border-slate-900 inline-flex items-center justify-center">
                <MessageSquare className="w-2 h-2 text-white" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">{b.sender_name}</p>
              <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed line-clamp-2 break-words">{b.content}</p>
              <p className="text-[10px] text-[#0047AB] dark:text-[#3B82F6] font-medium mt-0.5">Click to reply →</p>
            </div>
          </div>
        </button>
      ))}
      <style>{`
        @keyframes msgBubbleSlide {
          from { opacity: 0; transform: translateY(20px) scale(0.92); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
